import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Compass, Users } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import { TEAM, STATS } from "../data/mock";

const About = () => (
  <PageShell>
    <section className="bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-16 pb-16 relative">
        <Eyebrow light>Why RiskPedia exists</Eyebrow>
        <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight leading-[1.02]">The reference desk<br />Australian risk teams<br />never had.</h1>
        <p className="mt-7 text-[16px] text-white/60 max-w-2xl leading-relaxed">Regulatory intelligence in Australia is scattered across APRA letters, OAIC guidance, AUSTRAC rules and ACSC advisories. RiskPedia AU stitches it into one encyclopedia — written for practitioners, not lawyers.</p>
      </div>
    </section>

    <section className="py-20">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { icon: Target, h: "Mission", p: "Cut the time from regulatory change to board-ready action — from weeks of reading to minutes of matching." },
          { icon: Compass, h: "Method", p: "Every framework page follows the same skeleton: scope, controls, deadlines, penalties, and the Australian overlay." },
          { icon: Users, h: "Audience", p: "CROs, internal auditors, GRC consultants and the sales teams who serve them — five personas, one platform." },
        ].map((c) => (
          <div key={c.h} className="border border-neutral-200 rounded-lg p-7 hover:border-accent/40 hover:-translate-y-1 transition-[border-color,transform] duration-200">
            <c.icon size={24} className="text-accent" strokeWidth={1.7} />
            <h2 className="font-heading text-xl font-bold text-ink mt-4">{c.h}</h2>
            <p className="text-[13.5px] text-ink/60 leading-relaxed mt-2">{c.p}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="py-16 bg-paper">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200 rounded-lg overflow-hidden">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white p-8 text-center">
              <p className="font-heading text-4xl font-bold text-accent">{s.value}</p>
              <p className="text-[12.5px] text-ink/55 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
        <Eyebrow>The team</Eyebrow>
        <h2 className="font-heading text-4xl font-bold tracking-tight text-ink mb-10">Practitioners first.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEAM.map((t) => (
            <div key={t.name} data-testid={`team-card-${t.initial.toLowerCase()}`} className="border border-neutral-200 rounded-lg p-6 hover:border-accent/40 hover:-translate-y-1 transition-[border-color,transform] duration-200">
              <span className="w-14 h-14 rounded-full bg-ink text-white font-heading font-bold text-xl flex items-center justify-center">{t.initial}</span>
              <h3 className="font-heading text-lg font-bold text-ink mt-4">{t.name}</h3>
              <p className="font-mono text-[10.5px] tracking-[0.15em] uppercase text-accent mt-1">{t.role}</p>
              <p className="text-[13px] text-ink/55 leading-relaxed mt-3">{t.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
      <div className="max-w-[800px] mx-auto px-5 lg:px-8 text-center relative">
        <h2 className="font-heading text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.08]">Find your framework. Today.</h2>
        <p className="mt-4 text-[15px] text-white/60">Run the matcher, browse the hub, or just say hello.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/tools/cars-calculator" className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3.5 rounded-md text-[14px] font-semibold hover:bg-accent-dark transition-colors duration-200">Get started <ArrowRight size={15} /></Link>
          <Link to="/contact" data-testid="about-contact-cta" className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3.5 rounded-md text-[14px] font-semibold hover:border-white transition-colors duration-200">Contact us</Link>
        </div>
      </div>
    </section>
  </PageShell>
);

export default About;
